import { useRef } from "react";
import { Link } from "react-router-dom";
import { FaWhatsapp } from "react-icons/fa";
import { ArrowRight } from "lucide-react";
import SeplaHeroGate from "../components/SeplaHeroGate";
import { useCatalogue, displayName } from "../site/useCatalogue";
import ServeStrip from "../site/ServeStrip";
import { W2, Reveal, Counter, Magnetic } from "../site/fx";
import { useSEO, waLink } from "../seo";

const REASONS = [
  { t: "Food-grade stainless steel", d: "Food-grade, high-quality stainless steel for hygiene, corrosion resistance and lasting durability." },
  { t: "Reliable B2B supply", d: "Consistent quality and on-time delivery, whether you order 100 pieces or 10,000." },
  { t: "Customisation & OEM", d: "Custom design and private label, with size, finish, branding and packaging to your requirements." },
  { t: "Precise, elegant design", d: "Clean aesthetics and user-friendly details that improve presentation and service." },
  { t: "Factory-direct pricing", d: "Value that helps you manage budgets without compromising on quality." },
];

const FEATURED = ["SP-CD-01", "SP-JD8-02", "SP-PWAG-01", "SP-IBTH-01", "SP-CSD-01", "SP-DDSBSG-01", "SP-MB-01", "SP-TUS-02"];

export default function Home() {
  useSEO("Sepla — Stainless Steel Hotelware Manufacturer in Mumbai, 30+ Years", "Stainless steel hotelware manufacturer in Mumbai (Bhayandar, Thane) for over 30 years. Dispensers, buffet service, ice & bar, bowls and tabletop for hotels, restaurants and caterers across India. 100 to 10,000 pieces, OEM & private label.");
  const { categories, products } = useCatalogue();
  const dragRef = useRef(null);
  const dragging = useRef(null);

  const featured = FEATURED.map((c) => products.find((p) => p.code === c)).filter(Boolean);

  const onDragStart = (e) => {
    const x = e.clientX ?? e.touches?.[0]?.clientX;
    dragging.current = { x, left: dragRef.current.scrollLeft, moved: 0 };
  };
  const onDragMove = (e) => {
    if (!dragging.current) return;
    const x = e.clientX ?? e.touches?.[0]?.clientX;
    dragging.current.moved = Math.abs(x - dragging.current.x);
    dragRef.current.scrollLeft = dragging.current.left - (x - dragging.current.x);
  };
  const onDragEnd = () => { setTimeout(() => { dragging.current = null; }, 50); };
  const onCardClick = (e) => { if (dragging.current && dragging.current.moved > 6) e.preventDefault(); };

  return (
    <main data-testid="home-page">
      <SeplaHeroGate />

      {/* marquee */}
      <div className="marquee" aria-hidden="true">
        <div className="marquee-track">
          {[0, 1].map((k) => (
            <span key={k}>Dispensers • Buffet • Ice &amp; bar • Bowls • Tabletop • Food-grade steel • OEM &amp; private label •&nbsp;</span>
          ))}
        </div>
      </div>

      {/* category text cards */}
      <section className="sect sect-light" id="the-range" data-testid="category-bento">
        <div className="sect-in">
          <p className="kicker">The range</p>
          <W2 parts={[{ t: "Six lines. One standard:" }, { t: "professional.", em: true }]} className="sect-title" />
          <div className="range-cards">
            {categories.map((c) => (
              <Link key={c.slug} to={`/category/${c.slug}`} className="range-card" data-testid={`range-${c.slug}`}>
                <span className="range-num">{c.num}</span>
                <span className="range-arrow" aria-hidden="true">→</span>
                <span className="range-name">{displayName(c.name)}</span>
                <span className="range-count">{c.count} products</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 5 reasons */}
      <section className="sect sect-dark" data-testid="reasons">
        <div className="sect-in">
          <p className="kicker">Why choose us</p>
          <W2 parts={[{ t: "Made for the" }, { t: "pace", em: true }, { t: "of professional kitchens" }]} className="sect-title" />
          <Reveal className="reason-list">
            {REASONS.map((r, i) => (
              <article key={r.t} className="reason-row" data-testid={`reason-${i + 1}`}>
                <span className="reason-num">{String(i + 1).padStart(2, "0")}</span>
                <div>
                  <h3>{r.t}</h3>
                  <p>{r.d}</p>
                </div>
              </article>
            ))}
          </Reveal>
        </div>
      </section>

      <ServeStrip />

      {/* featured carousel */}
      <section className="sect sect-light" data-testid="featured">
        <div className="sect-in">
          <div className="row-between">
            <div>
              <p className="kicker">Featured</p>
              <W2 parts={[{ t: "Buffet" }, { t: "favourites", em: true }]} className="sect-title" />
            </div>
            <Link to="/products" className="btn btn-ghost">All products <ArrowRight size={14} /></Link>
          </div>
        </div>
        <div
          ref={dragRef}
          className="carousel"
          data-testid="featured-carousel"
          onMouseDown={onDragStart} onMouseMove={onDragMove} onMouseUp={onDragEnd} onMouseLeave={onDragEnd}
          onTouchStart={onDragStart} onTouchMove={onDragMove} onTouchEnd={onDragEnd}
        >
          {featured.map((p) => (
            <Link key={p.code} to={`/product/${p.code}`} className="caro-card" draggable={false} onClick={onCardClick}>
              <img src={p.image} alt={p.name} loading="lazy" draggable={false} />
              <span className="caro-code">{p.code}</span>
              <span className="caro-name">{displayName(p.name)}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* counters */}
      <section className="sect sect-dark counters" data-testid="counters">
        <div className="sect-in counters-grid">
          <div className="counter-cell"><Counter value={30} format={(n) => `${n}+`} /><span>years of family-run manufacturing in Mumbai</span></div>
          <div className="counter-cell"><Counter value={46} format={(n) => `${n}`} /><span>products in the range</span></div>
          <div className="counter-cell"><Counter value={6} /><span>categories</span></div>
          <div className="counter-cell"><Counter value={10000} format={(n) => `100 to ${n.toLocaleString("en-IN")}`} /><span>pieces per order</span></div>
        </div>
      </section>

      {/* CTA band (hero button lands here) */}
      <section className="cta-band" id="contact" data-testid="cta-band">
        <W2 parts={[{ t: "Need 100 pieces or 10,000?" }, { t: "Let's talk.", em: true }]} className="cta-title" />
        <Reveal className="cta-actions">
          <Magnetic><a className="btn btn-ink" href={waLink("Hi Sepla, we need a quote for our property.")} target="_blank" rel="noreferrer" data-testid="cta-whatsapp"><FaWhatsapp size={17} /> WhatsApp us</a></Magnetic>
          <Magnetic><Link className="btn btn-ink-o" to="/contact" data-testid="cta-quote">Get a quote</Link></Magnetic>
        </Reveal>
      </section>
    </main>
  );
}
